import type { Client, OperationContext } from 'urql';

import {
  type CustomRoleTaxonomySelection,
  ROLE_SELECTION_LIST_QUERY,
} from '@revelio/data-access';

import { stripVersionFromTaxonomyId } from '../../../next/CustomDash/filters/utils/taxonomyHelpers';
import type { RoleSelectionLists } from '../types';
import {
  getRoleSelectionListFromStore,
  type RoleSelectionListStore,
  roleSelectionListCache,
  setRoleSelectionListStore,
} from '../utils/cache';

/**
 * Main fetcher function that can be used by both CustomDash and deliverables
 */
export const fetchRoleSelectionListForTaxonomy = async ({
  taxonomySelection,
  gqlClient,
  context,
}: {
  taxonomySelection: CustomRoleTaxonomySelection;
  gqlClient: Client;
  context?: Partial<OperationContext>;
}): Promise<RoleSelectionLists | undefined> => {
  // Create a cache key from the taxonomy selection
  const cacheKey = JSON.stringify(taxonomySelection);

  // Check memory cache first
  const cachedRoleSelectionList = roleSelectionListCache.get(cacheKey);
  if (cachedRoleSelectionList) return cachedRoleSelectionList;

  // Check local storage
  const roleSelectionList = await getRoleSelectionListFromStore(cacheKey);
  if (roleSelectionList) {
    roleSelectionListCache.set(cacheKey, roleSelectionList);
    return roleSelectionList;
  }

  // Fetch from GraphQL
  const { data } = await gqlClient.query(
    ROLE_SELECTION_LIST_QUERY,
    {
      taxonomyId: stripVersionFromTaxonomyId(taxonomySelection),
    },
    context
  );

  if (data?.selectionList) {
    roleSelectionListCache.set(cacheKey, data.selectionList);
    await setRoleSelectionListStore({
      [cacheKey]: data.selectionList,
    } as RoleSelectionListStore);
    return data.selectionList;
  }

  return undefined;
};
